import React, { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Form, Input, AutoComplete, FormInstance, FormItemProps, Space } from 'antd';
import { css } from '@emotion/react';
import { useGPPracticesSearch } from '@components/Patients/Registration/utils/useGPPracticesSearch';
import styled from '@emotion/styled';
import useUser from '@vl-core/hooks/useUser';
import { useUsersGP } from '@components/Patients/Docman/MaybeGetDocmanConsent';

interface SurgeryODSFieldsProps {
  form: FormInstance;
  FormItemComponent: React.ComponentType<FormItemProps>;
  searchFieldName?: string;
  previewFieldName?: string;
  odsCodeFieldName?: string;
  searchRules?: any[];
  initialSearchValue?: string;
  disabled?: boolean;
  hidden?: boolean;
  isConsentGiven?: boolean;
  onSelect?: (practice: any) => void;
  isGPFormVisible?: boolean;
}

const gpAddressLookupRule = (isConsentGiven: boolean) => ({
  validator(_: any, value: string) {
    if (isConsentGiven && (!value || value.trim() === '')) {
      return Promise.reject(new Error('GP practice postcode is required when consent is given'));
    }
    return Promise.resolve();
  }
});

const gpAddressRequiredRule = (isGPFormVisible: boolean) => ({
  validator(_: any, value: string) {
    if (isGPFormVisible && (!value || value.trim() === '')) {
      return Promise.reject(new Error('GP practice is required'));
    }
    return Promise.resolve();
  }
});

const isValidPostcode = (postcode: string): boolean => {
  if (!postcode) return false;
  const postcodeRegex = /^[A-Z]{1,2}\d[\dA-Z]?\s?\d[A-Z]{2}$/i;
  return postcodeRegex.test(postcode);
};

const postcodeRule = () => ({
  validator(_: any, value: string) {
    if (value && !isValidPostcode(value)) {
      return Promise.reject(new Error('Please enter a valid postcode'));
    }
    return Promise.resolve();
  }
});

export const SurgeryODSFields: React.FC<SurgeryODSFieldsProps> = ({
  form,
  FormItemComponent = Form.Item,
  searchFieldName = 'gp_address_lookup',
  previewFieldName = 'gp_address_preview',
  odsCodeFieldName = 'gp_ods_code',
  searchRules = [postcodeRule()],
  initialSearchValue,
  disabled = false,
  hidden = false,
  isConsentGiven = false,
  onSelect,
  isGPFormVisible = false
}) => {
  const { t } = useTranslation('common');
  const [searchGPPostcode, setSearchGPPostcode] = useState<string>('');
  const { data: gpPracticesData } = useGPPracticesSearch(searchGPPostcode);
  const user = useUser();
  const isNHSLoginUser = user?.ident_prov_code === 'NHSLOGIN';
  const gpDetails = useUsersGP();

  const handleSearch = useCallback((postcode: string) => {
    const upperPostcode = postcode.toUpperCase();
    if (isValidPostcode(upperPostcode)) {
      setSearchGPPostcode(upperPostcode);
    }
  }, []);

  const handleSelect = useCallback(
    (index: number) => {
      const selectedPractice = gpPracticesData?.[index];
      if (selectedPractice) {
        form.setFieldsValue({
          [searchFieldName]: '',
          [previewFieldName]: selectedPractice.label,
          [odsCodeFieldName]: selectedPractice.ods_code,
          docman_active: selectedPractice.docman_active
        });

        onSelect?.(selectedPractice);
      }
    },
    [gpPracticesData, form, searchFieldName, previewFieldName, odsCodeFieldName, onSelect]
  );

  const handleDropdownVisibleChange = useCallback(
    (visible: boolean) => {
      if (visible) {
        const currentValue = form.getFieldValue(searchFieldName);
        if (currentValue && isValidPostcode(currentValue)) {
          setSearchGPPostcode(currentValue.toUpperCase());
        }
      }
    },
    [form, searchFieldName]
  );

  const inputStyles = css`
    width: 100%;
    .ant-select-selector {
      background-color: transparent !important;
    }
    input {
      text-transform: uppercase;
    }
  `;

  const PracticeAddress = styled(Input)`
    &[readonly] {
      color: var(--productGray) !important;
      background-color: rgba(0, 0, 0, 0.05);
    }
  `;

  return (
    <>
      <FormItemComponent
        label={t('Search by postcode for your GP')}
        labelAlign="left"
        required
        name={searchFieldName}
        initialValue={initialSearchValue}
        rules={searchRules}
        hidden={
          hidden ||
          !!gpDetails?.ro_fields?.includes('gp_ods_code') ||
          (isNHSLoginUser && !!gpDetails && !gpDetails?.ro_fields?.includes('gp_ods_code'))
        }
      >
        <AutoComplete
          options={
            gpPracticesData?.map((practice, i) => ({
              value: i.toString(),
              label: practice.label
            })) || []
          }
          onSearch={handleSearch}
          onSelect={handleSelect}
          onDropdownVisibleChange={handleDropdownVisibleChange}
          disabled={disabled}
          css={inputStyles}
          getPopupContainer={(triggerNode) => triggerNode.parentElement}
        />
      </FormItemComponent>

      <div
        css={css`
          display: flex;
          flex-direction: column;
        `}
      >
        <FormItemComponent label={t('Selected GP Practice')} labelAlign="left" required>
          <Space.Compact style={{ display: 'flex', width: '100%' }}>
            <FormItemComponent
              name={previewFieldName}
              noStyle
              rules={[
                ...(isConsentGiven ? [gpAddressLookupRule(isConsentGiven)] : []),
                gpAddressRequiredRule(isGPFormVisible)
              ]}
            >
              <PracticeAddress readOnly required style={{ flex: 1 }} />
            </FormItemComponent>
          </Space.Compact>
        </FormItemComponent>
      </div>
    </>
  );
};

export default SurgeryODSFields;
